import { MouseEvent as ReactMouseEvent, ReactElement } from 'react'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import { ElementListItem } from '../types'

interface ElementRowProps {
  item: ElementListItem
  selected: boolean
  selectable: boolean
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void
}

/** A single, compact element row: label on top, cross-section / assembly underneath. */
const ElementRow = ({ item, selected, selectable, onClick }: ElementRowProps): ReactElement => {
  const theme = useTheme()
  const sectionInfo = item.crossSectionLabel ?? item.assemblyName

  return (
    <Box
      role={selectable ? 'button' : undefined}
      onClick={selectable ? onClick : undefined}
      data-cy={`element-list-row-${item.id}`}
      data-selected={selected ? 'true' : 'false'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        width: '100%',
        height: '100%',
        textAlign: 'left',
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 1,
        background: selected ? theme.palette.action.selected : 'transparent',
        cursor: selectable ? 'pointer' : 'default',
        '&:hover': selectable ? { background: theme.palette.action.hover } : undefined,
      }}
    >
      <Typography variant="body2" noWrap sx={{ fontWeight: 500, minWidth: 0 }} title={item.label}>
        {item.label}
      </Typography>
      <Tooltip title={sectionInfo ?? ''} arrow disableInteractive>
        <Typography
          variant="caption"
          noWrap
          sx={{ color: theme.palette.text.secondary, minWidth: 0 }}
        >
          {sectionInfo ?? '—'}
        </Typography>
      </Tooltip>
    </Box>
  )
}

export default ElementRow
